import styled from '@emotion/styled';
import * as qrcode from 'qrcode.react';
import { useEffect, useMemo, useState } from 'react';
import { useRoute } from 'wouter';
import { typography } from '~/modules/Elements/cssMixins';
import styles from '~/modules/GameEngine/Drawing/styles';
import RemoteMicServer from '~/modules/RemoteMic/Network/Server';
import useQueryParam from '~/modules/hooks/useQueryParam';

const { QRCodeSVG } = qrcode;

function RoomCode({ gameCode, ...props }: { gameCode: string } & React.HTMLAttributes<HTMLElement>) {
  return (
    <GameCode {...props} aria-label={`Game code ${gameCode}`}>
      {gameCode.split('').map((letter, i) => (
        <span key={i}>{letter}</span>
      ))}
    </GameCode>
  );
}

function ConnectRemoteMic() {
  const [copied, setCopied] = useState(false);

  const linkObject = useMemo(() => new URL(global.location?.href), []);
  const [match] = useRoute('remote-mic');

  const gameCode = useQueryParam('room') ?? RemoteMicServer.getGameCode();

  linkObject.pathname = `${import.meta.env.BASE_URL}remote-mic`;
  linkObject.search = `room=${gameCode}`;

  const link = linkObject.href;

  useEffect(() => {
    if (!match) {
      RemoteMicServer.start();
    }
  }, [match]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      // fallback: selecciona el input (si clipboard no está disponible)
      const el = document.querySelector<HTMLInputElement>('[data-test="server-link-input"]');
      el?.focus();
      el?.select();
    }
  };

  return (
    <Shell>
      <GlassCard>
        <TopBar>
          <Title>
            Connect microphones
            <SmallMuted>Scan QR or use the code</SmallMuted>
          </Title>

          <CodeLine>
            Code: <RoomCode gameCode={gameCode} data-test="game-code" />
          </CodeLine>
        </TopBar>

        <Grid>
          <QRPane>
            <QRFrame>
              <QRCodeSVG value={link} width="100%" height="100%" includeMargin />
            </QRFrame>

            <SmallHint>
              Open{' '}
              <HostLink href={linkObject.origin} target="_blank" rel="noreferrer">
                {linkObject.host}
              </HostLink>{' '}
              on your phone
            </SmallHint>
          </QRPane>

          <InfoPane>
            <SectionTitle>Steps</SectionTitle>
            <Steps>
              <li>
                Go to{' '}
                <HostLink href={linkObject.origin} target="_blank" rel="noreferrer">
                  {linkObject.host}
                </HostLink>{' '}
                on your phone
              </li>
              <li>
                Click on <b>Join game</b>
              </li>
              <li>
                Enter the code <RoomCode gameCode={gameCode} />
              </li>
              <li>Follow the instructions</li>
            </Steps>

            <Divider />

            <SectionTitle>Or copy &amp; send link</SectionTitle>
            <InputCopyContainer>
              <InputCopy readOnly value={link} data-test="server-link-input" />
              <CopyButton type="button" onClick={copy} aria-label="Copy link">
                {copied ? 'Copied' : 'Copy'}
              </CopyButton>
            </InputCopyContainer>
          </InfoPane>
        </Grid>
      </GlassCard>
    </Shell>
  );
}

/* ------------------ layout wrappers ------------------ */

const Shell = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  padding: 1.25rem;

  @media (max-width: 560px) {
    padding: 0.75rem;
    align-items: flex-start;
  }
`;

const GlassCard = styled.div`
  width: 100%;
  max-width: 72rem;

  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(12px);
  border-radius: 1.25rem;

  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.08);
  padding: 1.25rem 1.25rem 1.5rem;

  color: white;

  @media (max-width: 560px) {
    border-radius: 1rem;
    padding: 1rem;
  }
`;

const TopBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const Title = styled.div`
  ${typography};
  font-weight: 900;
  font-size: 2.2rem;
  letter-spacing: 0.03em;

  @media (max-width: 560px) {
    font-size: 1.6rem;
  }
`;

const SmallMuted = styled.div`
  margin-top: 0.25rem;
  font-size: 1.1rem;
  opacity: 0.85;

  @media (max-width: 560px) {
    font-size: 1rem;
  }
`;

const CodeLine = styled.div`
  ${typography};
  font-size: 1.6rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  @media (max-width: 560px) {
    font-size: 1.2rem;
  }
`;

/* ------------------ grid ------------------ */

const Grid = styled.div`
  display: grid;
  grid-template-columns: 0.95fr 1.05fr;
  gap: 1.25rem;
  align-items: start;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const QRPane = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: center;
`;

const QRFrame = styled.div`
  width: min(28rem, 100%);
  aspect-ratio: 1 / 1;

  background: rgba(255, 255, 255, 0.95);
  border-radius: 1rem;
  overflow: hidden;

  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 92% !important;
    height: 92% !important;
  }

  @media (max-width: 560px) {
    width: min(22rem, 100%);
    border-radius: 0.9rem;
  }
`;

const SmallHint = styled.div`
  ${typography};
  font-size: 1.1rem;
  opacity: 0.9;
  text-align: center;

  @media (max-width: 560px) {
    font-size: 1rem;
  }
`;

const InfoPane = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
`;

const SectionTitle = styled.h3`
  margin: 0;
  ${typography};
  font-weight: 900;
  font-size: 1.35rem;
  color: ${styles.colors.text.active};

  @media (max-width: 560px) {
    font-size: 1.2rem;
  }
`;

const Steps = styled.ol`
  margin: 0;
  padding-left: 1.25rem;
  display: grid;
  gap: 0.55rem;

  li {
    color: white;
    font-size: 1.25rem;
    line-height: 1.65rem;
  }

  b {
    font-weight: 900;
    ${typography};
    color: ${styles.colors.text.active};
  }

  @media (max-width: 560px) {
    padding-left: 1.1rem;

    li {
      font-size: 1.05rem;
      line-height: 1.45rem;
    }
  }
`;

const Divider = styled.hr`
  margin: 0.25rem 0;
  border: 0;
  height: 1px;
  background: rgba(255, 255, 255, 0.12);
`;

/* ------------------ code + input ------------------ */

const HostLink = styled.a`
  color: ${styles.colors.text.active};
  font-weight: 900;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const GameCode = styled.strong`
  text-transform: uppercase;
  display: inline-flex;
  gap: 0.75rem;
  padding: 0.35rem 0.85rem;

  border-radius: 0.75rem;

  background: rgba(0, 0, 0, 0.35);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.12);

  ${typography};
  font-weight: 900;
  letter-spacing: 0.18em;

  span {
    display: inline-block;
    min-width: 0.85rem;
    text-align: center;
  }

  @media (max-width: 560px) {
    gap: 0.55rem;
    padding: 0.3rem 0.7rem;
    border-radius: 0.65rem;
    letter-spacing: 0.14em;
  }
`;

const InputCopyContainer = styled.div`
  position: relative;
  width: 100%;
`;

const InputCopy = styled.input`
  width: 100%;
  box-sizing: border-box;

  padding: 0.95rem 7.25rem 0.95rem 1rem; /* espacio para el botón */
  border: 0;
  border-radius: 999px;

  background: rgba(255, 255, 255, 0.08);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.12) inset;

  color: white;
  font-size: 1rem;

  outline: none;

  &:focus {
    box-shadow: 0 0 0 2px rgba(255, 165, 0, 0.5) inset;
  }

  @media (max-width: 560px) {
    padding: 0.9rem 6.5rem 0.9rem 0.95rem;
    font-size: 0.95rem;
  }
`;

const CopyButton = styled.button`
  cursor: pointer;
  position: absolute;
  right: 0.4rem;
  top: 50%;
  transform: translateY(-50%);

  height: calc(100% - 0.8rem);
  padding: 0 1.4rem;

  border: 0;
  border-radius: 999px;

  background: ${styles.colors.text.active};

  ${typography};
  font-weight: 900;
  font-size: 1.05rem;
  color: black;

  transition:
    transform 120ms,
    filter 120ms;

  &:hover {
    filter: brightness(1.05);
    transform: translateY(-50%) scale(1.02);
  }

  &:active {
    filter: brightness(0.95);
    transform: translateY(-50%) scale(0.98);
  }

  @media (max-width: 560px) {
    padding: 0 1.2rem;
    font-size: 1rem;
  }
`;

export default ConnectRemoteMic;
